import { useState, useId } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(50),
  lastName: z.string().min(1, "Last name is required").max(50),
  phone: z.string()
    .min(10, "Phone number must be at least 10 digits")
    .regex(/^[\d\s\-\(\)]+$/, "Please enter a valid phone number"),
  email: z.string().email("Please enter a valid email address"),
  address: z.string().min(5, "Please enter your full address"),
  city: z.string().min(2, "City is required"),
  state: z.string()
    .min(2, "State is required")
    .refine((state) => state.toUpperCase() === 'MN' || state.toUpperCase() === 'MINNESOTA', "Sorry, we only serve Minnesota"),
  zipCode: z.string()
    .min(5, "ZIP code is required")
    .max(10, "Invalid ZIP code"),
  // Honeypot field - should always be empty
  website: z.string().max(0).optional(),
});

type FormData = z.infer<typeof formSchema>;

export const HeroForm = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStartTime] = useState(Date.now());
  const formId = useId();
  
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
  });

  const onSubmit = async (data: FormData) => {
    // Bot detection: Check honeypot field
    if (data.website && data.website.length > 0) {
      // Silently reject - bot filled in honeypot
      toast.success("Thank you! We'll contact you shortly to schedule your service.");
      reset();
      return;
    }

    // Bot detection: Check if form was submitted too quickly (less than 2 seconds)
    const timeTaken = Date.now() - formStartTime;
    if (timeTaken < 2000) {
      // Silently reject - likely a bot
      toast.success("Thank you! We'll contact you shortly to schedule your service.");
      reset();
      return;
    }

    setIsSubmitting(true);
    
    try {
      // Save to Supabase
      const { error } = await supabase.from("form_leads").insert({
        first_name: data.firstName,
        last_name: data.lastName,
        phone: data.phone,
        email: data.email,
        address: data.address,
        city: data.city,
        state: data.state,
        zip_code: data.zipCode,
        source: "hero_form",
      });

      if (error) {
        console.error("Error saving lead:", error);
        toast.error("Something went wrong. Please try again or call us directly.");
        return;
      }

      // Forward to Make.com webhook (fire and forget - don't block on this)
      fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/make-webhook`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          firstName: data.firstName,
          lastName: data.lastName,
          phone: data.phone,
          email: data.email,
          address: data.address,
          city: data.city,
          state: data.state,
          zipCode: data.zipCode,
          source: "hero_form",
        }),
      }).catch(() => {
        // Silently ignore webhook errors - form submission already succeeded
      });

      toast.success("Thank you! We'll contact you shortly to schedule your service.");
      reset();
    } catch (err) {
      console.error("Error:", err);
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="bg-card/95 backdrop-blur-sm rounded-2xl p-6 shadow-2xl border border-border/50 w-full max-w-md">
      <div className="text-center mb-5">
        <h2 className="text-xl font-heading font-bold text-foreground">Get Service Now</h2>
        <p className="text-sm text-muted-foreground mt-1">Same-day appointments available</p>
      </div>
      
      <form onSubmit={handleSubmit(onSubmit)} className="space-y-3" aria-label="Request service form">
        {/* Honeypot field - hidden from humans, visible to bots */}
        <div className="absolute left-[-9999px]" aria-hidden="true">
          <label htmlFor={`${formId}-website`}>Website (leave blank)</label>
          <Input
            id={`${formId}-website`}
            type="text"
            tabIndex={-1}
            autoComplete="off"
            {...register("website")}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label htmlFor={`${formId}-firstName`} className="sr-only">First Name</label>
            <Input
              id={`${formId}-firstName`}
              placeholder="First Name"
              autoComplete="given-name"
              {...register("firstName")}
              className={errors.firstName ? "border-destructive" : ""}
              aria-invalid={errors.firstName ? "true" : "false"}
              aria-describedby={errors.firstName ? `${formId}-firstName-error` : undefined}
            />
            {errors.firstName && (
              <p id={`${formId}-firstName-error`} className="text-xs text-destructive mt-1" role="alert">
                {errors.firstName.message}
              </p>
            )}
          </div>
          <div>
            <label htmlFor={`${formId}-lastName`} className="sr-only">Last Name</label>
            <Input
              id={`${formId}-lastName`}
              placeholder="Last Name"
              autoComplete="family-name"
              {...register("lastName")}
              className={errors.lastName ? "border-destructive" : ""}
              aria-invalid={errors.lastName ? "true" : "false"}
              aria-describedby={errors.lastName ? `${formId}-lastName-error` : undefined}
            />
            {errors.lastName && (
              <p id={`${formId}-lastName-error`} className="text-xs text-destructive mt-1" role="alert">
                {errors.lastName.message}
              </p>
            )}
          </div>
        </div>

        <div>
          <label htmlFor={`${formId}-phone`} className="sr-only">Phone Number</label>
          <Input
            id={`${formId}-phone`}
            type="tel"
            placeholder="Phone Number"
            autoComplete="tel"
            {...register("phone")}
            className={errors.phone ? "border-destructive" : ""}
            aria-invalid={errors.phone ? "true" : "false"}
            aria-describedby={errors.phone ? `${formId}-phone-error` : undefined}
          />
          {errors.phone && (
            <p id={`${formId}-phone-error`} className="text-xs text-destructive mt-1" role="alert">
              {errors.phone.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor={`${formId}-email`} className="sr-only">Email Address</label>
          <Input
            id={`${formId}-email`}
            type="email"
            placeholder="Email Address"
            autoComplete="email"
            {...register("email")}
            className={errors.email ? "border-destructive" : ""}
            aria-invalid={errors.email ? "true" : "false"}
            aria-describedby={errors.email ? `${formId}-email-error` : undefined}
          />
          {errors.email && (
            <p id={`${formId}-email-error`} className="text-xs text-destructive mt-1" role="alert">
              {errors.email.message}
            </p>
          )}
        </div>

        <div>
          <label htmlFor={`${formId}-address`} className="sr-only">Street Address</label>
          <Input
            id={`${formId}-address`}
            placeholder="Street Address"
            autoComplete="street-address"
            {...register("address")}
            className={errors.address ? "border-destructive" : ""}
            aria-invalid={errors.address ? "true" : "false"}
            aria-describedby={errors.address ? `${formId}-address-error` : undefined}
          />
          {errors.address && (
            <p id={`${formId}-address-error`} className="text-xs text-destructive mt-1" role="alert">
              {errors.address.message}
            </p>
          )}
        </div>

        <div className="grid grid-cols-3 gap-3">
          <div>
            <label htmlFor={`${formId}-city`} className="sr-only">City</label>
            <Input
              id={`${formId}-city`}
              placeholder="City"
              autoComplete="address-level2"
              {...register("city")}
              className={errors.city ? "border-destructive" : ""}
              aria-invalid={errors.city ? "true" : "false"}
              aria-describedby={errors.city ? `${formId}-city-error` : undefined}
            />
            {errors.city && (
              <p id={`${formId}-city-error`} className="text-xs text-destructive mt-1" role="alert">
                {errors.city.message}
              </p>
            )}
          </div>
          <div>
            <label htmlFor={`${formId}-state`} className="sr-only">State</label>
            <Input
              id={`${formId}-state`}
              placeholder="State"
              autoComplete="address-level1"
              {...register("state")}
              className={errors.state ? "border-destructive" : ""}
              aria-invalid={errors.state ? "true" : "false"}
              aria-describedby={errors.state ? `${formId}-state-error` : undefined}
            />
            {errors.state && (
              <p id={`${formId}-state-error`} className="text-xs text-destructive mt-1" role="alert">
                {errors.state.message}
              </p>
            )}
          </div>
          <div>
            <label htmlFor={`${formId}-zip`} className="sr-only">ZIP Code</label>
            <Input
              id={`${formId}-zip`}
              placeholder="ZIP"
              autoComplete="postal-code"
              {...register("zipCode")}
              className={errors.zipCode ? "border-destructive" : ""}
              aria-invalid={errors.zipCode ? "true" : "false"}
              aria-describedby={errors.zipCode ? `${formId}-zip-error` : undefined}
            />
            {errors.zipCode && (
              <p id={`${formId}-zip-error`} className="text-xs text-destructive mt-1" role="alert">
                {errors.zipCode.message}
              </p>
            )}
          </div>
        </div>

        <Button
          type="submit"
          disabled={isSubmitting}
          className="w-full bg-secondary hover:bg-secondary/90 text-secondary-foreground font-semibold py-6"
          aria-busy={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" aria-hidden="true" />
              Submitting...
            </>
          ) : (
            <>
              Request Service <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" />
            </>
          )}
        </Button>
        
      </form>
    </div>
  );
};
